import { createMintSocketBuilder } from '@mint-lib/api-zod-support/socket';
import { z } from 'zod';

import {
  PlacementFolderSchemaMdm,
  PlacementFolderSchemaOld,
} from '../shared/schemas/placementFolder.js';
import { PricingItemSchemaMdm } from '../shared/schemas/pricingItem.js';

export const socket = createMintSocketBuilder();

const roomPayloadSchema = z.object({
  room: z.literal('general'),
  entityId: z.string(),
});

// TODO(ghostMdm): drop the `Old` branch with `ff.ghostMdm.enabled` and keep only
// `PlacementFolderSchemaMdm` (URN-keyed `attributes`).
export const createdPlacementFoldersPayloadSchema = z
  .array(PlacementFolderSchemaMdm)
  .or(z.array(PlacementFolderSchemaOld));

type CreatedPlacementFolderRow = z.infer<
  typeof createdPlacementFoldersPayloadSchema
>[number];

type CreatedPlacementFolderMdm = z.infer<typeof PlacementFolderSchemaMdm>;

export const isCreatedPlacementFolderMdm = (
  row: CreatedPlacementFolderRow,
): row is CreatedPlacementFolderMdm => !('dateFrom' in row.attributes);

// TODO(ghostMdm): remove `updatedPlacementFoldersPayloadSchemaOld` with `ff.ghostMdm.enabled`.
const updatedPlacementFoldersPayloadSchemaOld = z.object({
  placementFolderIds: z.array(z.string()),
  name: z.string().nullable().optional(),
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
  price: z.string().nullable().optional(),
  priceType: z.string().nullable().optional(),
  userDefinedId: z.string().nullable().optional(),
});

export const updatedPlacementFoldersPayloadSchemaMdm = z.array(
  PlacementFolderSchemaMdm,
);

export const updatedPlacementFoldersPayloadSchema =
  updatedPlacementFoldersPayloadSchemaMdm.or(
    updatedPlacementFoldersPayloadSchemaOld,
  );

export const deletedPlacementFoldersPayloadSchema = z.object({
  placementFolderIds: z.array(z.string()),
});

// TODO(ghostMdm): remove `updatedPricingItemsPayloadSchemaOld` with `ff.ghostMdm.enabled`.
const updatedPricingItemsPayloadSchemaOld = z.object({
  pricingItemIds: z.array(z.string()),
  price: z.string().nullable(),
});

export const updatedPricingItemsPayloadSchemaMdm =
  z.array(PricingItemSchemaMdm);

export const updatedPricingItemsPayloadSchema =
  updatedPricingItemsPayloadSchemaMdm.or(updatedPricingItemsPayloadSchemaOld);

type UpdatedPricingItemsPayload = z.infer<
  typeof updatedPricingItemsPayloadSchema
>;
type UpdatedPricingItemsPayloadMdm = z.infer<
  typeof updatedPricingItemsPayloadSchemaMdm
>;

export const isUpdatedPricingItemsPayloadMdm = (
  payload: UpdatedPricingItemsPayload,
): payload is UpdatedPricingItemsPayloadMdm => Array.isArray(payload);

export const joinRoom = socket.defineSend({
  topic: 'join',
  payloadSchema: roomPayloadSchema,
});
export const leaveRoom = socket.defineSend({
  topic: 'leave',
  payloadSchema: roomPayloadSchema,
});

export const onJoinFailed = socket.defineSubscribe({
  topic: 'join_failed',
  payloadSchema: z.unknown(),
});
export const onPlacementFoldersCreated = socket.defineSubscribe({
  topic: 'placement_folders_created',
  payloadSchema: createdPlacementFoldersPayloadSchema,
});
export const onPlacementFoldersUpdated = socket.defineSubscribe({
  topic: 'placement_folders_updated',
  payloadSchema: updatedPlacementFoldersPayloadSchema,
});
export const onPlacementFoldersDeleted = socket.defineSubscribe({
  topic: 'placement_folders_deleted',
  payloadSchema: deletedPlacementFoldersPayloadSchema,
});
export const onPricingItemsUpdated = socket.defineSubscribe({
  topic: 'pricing_items_updated',
  payloadSchema: updatedPricingItemsPayloadSchema,
});
